import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { requestPasswordResetSchema } from "@tc/shared";
import { Button, Icon, LogoIcon, TextInput } from "@/components/ui";
import { adminApi, ApiError } from "@/features/clubs/api";

interface LocationState {
  email?: string;
}

/**
 * Public "forgot password" request page, reached from the login screen's "Forgot password?" link
 * (which passes the typed email through router state to prefill this form). Sits beside /login —
 * outside the role shells — and renders in the globally-initialized language, like LoginPage.
 *
 * The response is always the same "check your email" confirmation whether or not the address
 * matches an account: the server endpoint is enumeration-safe, so we never reveal which emails
 * exist. We only block on a malformed email (client-side) or a transport failure.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState((location.state as LocationState | null)?.email ?? "");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = requestPasswordResetSchema.safeParse({ email });
    if (!parsed.success) {
      setError(t("auth.errEmail"));
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.requestPasswordReset(parsed.data.email);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("auth.reset.requestError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-stage">
      <div className="auth-shell">
        <div className="auth-head">
          <div className="auth-wordmark">
            <LogoIcon style={{ height: 38 }} />
            <div style={{ textAlign: "left" }}>
              <div className="wm-name">Travel Connection</div>
              <div className="auth-tagline">{t("auth.tagline")}</div>
            </div>
          </div>
        </div>

        <div className="auth-card">
          {sent ? (
            <>
              <h1>{t("auth.reset.sentTitle")}</h1>
              <p className="auth-sub">{t("auth.reset.sentBody", { email })}</p>
              <Button size="lg" full style={{ marginTop: 22 }} onClick={() => navigate("/login")}>
                {t("auth.reset.backToLogin")}
              </Button>
            </>
          ) : (
            <>
              <h1>{t("auth.reset.requestTitle")}</h1>
              <p className="auth-sub">{t("auth.reset.requestSubtitle")}</p>

              {error && (
                <div className="auth-alert" style={{ marginTop: 20 }} role="alert">
                  <Icon name="alert" size={17} className="aa-ic" />
                  <div className="aa-body">{error}</div>
                </div>
              )}

              <form className="auth-form" onSubmit={onSubmit} noValidate>
                <div>
                  <label className="auth-field-label" htmlFor="reset-email">
                    {t("auth.email")}
                  </label>
                  <TextInput
                    id="reset-email"
                    icon="mail"
                    type="email"
                    value={email}
                    placeholder={t("auth.emailPlaceholder")}
                    autoComplete="username"
                    autoFocus
                    onChange={setEmail}
                  />
                </div>

                <Button type="submit" size="lg" full disabled={submitting}>
                  {submitting ? t("auth.reset.sending") : t("auth.reset.send")}
                </Button>
              </form>

              <div className="auth-foot" style={{ marginTop: 18 }}>
                <button type="button" className="link" onClick={() => navigate("/login")}>
                  {t("auth.reset.backToLogin")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="auth-pagefoot">
        <span>© {new Date().getFullYear()} Travel Connection Spain</span>
      </div>
    </main>
  );
}
